import { Component, computed, effect, ElementRef, HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DomHandler } from 'primeng/dom';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { LayoutService } from '@/layout/service/layout.service';

@Component({
    selector: '[app-menuitem]',
    imports: [CommonModule, RouterModule, RippleModule, TooltipModule],
    template: `
        <ng-container>
            <div *ngIf="root && isVisible" class="layout-menuitem-root-text">
                @if(item.url){
                    <a [href]="item.url" target="_blank">{{ item.label }}</a>
                } @else if (!item.url && !item.routerLink) {
                    <a class="layout-menuitem-root-label">{{ item.label }}</a>
                } @else {
                    <a
                    *ngIf="item.routerLink"
                    [routerLink]="item.routerLink"
                    routerLinkActive="active-menuitem"
                    [routerLinkActiveOptions]="
                        item.routerLinkActiveOptions || {
                            paths: 'exact',
                            queryParams: 'ignored',
                            matrixParams: 'ignored',
                            fragment: 'ignored'
                        }
                    "
                >
                    {{ item.label }}
                </a>
                }
                                
            </div>
            <a
                *ngIf="(!item.routerLink || item.items) && isVisible"
                [attr.href]="item.url"
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                (mouseleave)="onMouseLeave()"
                [ngClass]="item.class"
                [attr.target]="item.target"
                tabindex="0"
                pRipple
                [pTooltip]="item.label"
                [appendTo]="'body'"
                [tooltipZIndex]="'20000'"
                [tooltipDisabled]="!shouldShowTooltip()"
            >
                <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
                <span class="layout-menuitem-text label-small text-inherit">{{ item.label }}</span>
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
            </a>
            <a
                *ngIf="item.routerLink && !item.items && isVisible"
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                (mouseleave)="onMouseLeave()"
                [ngClass]="item.class"
                [routerLink]="item.routerLink"
                routerLinkActive="active-route"
                [routerLinkActiveOptions]="
                    item.routerLinkActiveOptions || {
                        paths: 'exact',
                        queryParams: 'ignored',
                        matrixParams: 'ignored',
                        fragment: 'ignored'
                    }
                "
                [fragment]="item.fragment"
                [queryParamsHandling]="item.queryParamsHandling"
                [preserveFragment]="item.preserveFragment"
                [skipLocationChange]="item.skipLocationChange"
                [replaceUrl]="item.replaceUrl"
                [state]="item.state"
                [queryParams]="item.queryParams"
                [attr.target]="item.target"
                tabindex="0"
                pRipple
                [pTooltip]="item.label"
                [appendTo]="'body'"
                [tooltipZIndex]="'20000'"
                [tooltipDisabled]="!shouldShowTooltip()"
            >
                <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
                <span class="layout-menuitem-text label-small text-inherit">{{ item.label }}</span>
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
            </a>

            <ul #submenu class="layout-root-submenulist !z-50" *ngIf="item.items && isVisible" [@children]="submenuAnimation" (@children.done)="onSubmenuAnimated($event)" (mouseenter)="onSubmenuMouseEnter()" (mouseleave)="onSubmenuMouseLeave()">
                <li *ngIf="root && isCompact()" class="layout-submenu-header">
                    <span class="layout-submenu-header-text">{{ item.label }}</span>
                </li>
                <ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
                    <li app-menuitem *ngIf="isVisibleItem(child)" [item]="child" [index]="i" [parentKey]="key" [class]="child['badgeClass']"></li>
                </ng-template>
            </ul>
        </ng-container>
    `,
    animations: [
        trigger('children', [
            state(
                'collapsed',
                style({
                    height: '0'
                })
            ),
            state(
                'expanded',
                style({
                    height: '*'
                })
            ),
            state(
                'hidden',
                style({
                    display: 'none'
                })
            ),
            state(
                'visible',
                style({
                    display: 'block'
                })
            ),
            transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ]
})
export class AppMenuitem implements OnInit, OnChanges, OnDestroy {
    @Input() item: any;

    @Input() index!: number;

    @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

    @Input() parentKey!: string;

    @ViewChild('submenu') submenu!: ElementRef;

    @HostBinding('class.active-menuitem')
    get activeClass() {
        return this.active && !this.root;
    }

    /* Route-derived highlight, kept separate from `active` (which in
       horizontal/slim/compact only means "submenu is open"). A root item is
       route-active whenever one of its children is, so the menu bar shows
       where you are even while its popover is closed. */
    @HostBinding('class.active-route-item')
    routeActive = false;

    /* The host <li> is created by the parent regardless of `visible`, so an
       item that renders nothing would still take up a slot (and the menu bar's
       0.25rem gap) in the row. */
    @HostBinding('style.display')
    get hostDisplay(): string | null {
        return this.isVisible ? null : 'none';
    }

    get isVisible(): boolean {
        return this.isVisibleItem(this.item);
    }

    /* `visible` is optional, and callers routinely compute it from a nullable
       value — `visible: clientId && hasPrivilege(...)` yields null, not false,
       when no client is selected. Only an absent flag means visible; every
       other falsy value (false, null, '', 0) hides the item. */
    isVisibleItem(item: any): boolean {
        if (!item) return false;
        return item.visible === undefined ? true : !!item.visible;
    }

    active = false;

    menuSourceSubscription: Subscription;

    menuResetSubscription: Subscription;

    clearHoverTimeoutSubscription: Subscription;

    routerEventsSubscription: Subscription;

    key: string = '';

    // Hover state tracking for submenu
    private hoverTimeout: any;
    private isHoveringMenuItem = false;
    private isHoveringSubmenu = false;

    get submenuAnimation() {
        if (this.layoutService.isDesktop() && (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isCompact())) {
            return this.active ? 'visible' : 'hidden';
        } else return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
    }

    isSlim = computed(() => this.layoutService.isSlim());

    isCompact = computed(() => this.layoutService.isCompact());

    isHorizontal = computed(() => this.layoutService.isHorizontal());

    get isDesktop() {
        return this.layoutService.isDesktop();
    }

    get isMobile() {
        return this.layoutService.isMobile();
    }

    constructor(
        public layoutService: LayoutService,
        public router: Router,
        private el: ElementRef
    ) {
        this.menuSourceSubscription = this.layoutService.menuSource$.subscribe((value) => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
                } else {
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active = false;
                    }
                }
            });
        });

        this.menuResetSubscription = this.layoutService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.clearHoverTimeoutSubscription = this.layoutService.clearHoverTimeoutSource$.subscribe(() => {
            this.clearHoverTimeout();
        });

        this.routerEventsSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.updateRouteActive();

            if (this.isCompact() || this.isSlim() || this.isHorizontal()) {
                this.active = false;
            } else {
                if (this.item.routerLink) {
                    this.updateActiveStateFromRoute();
                }
            }
        });

        effect(() => {
            if (this.layoutService.isOverlay() && this.layoutService.isSidebarActive()) {
                if (this.item.routerLink) {
                    this.updateActiveStateFromRoute();
                }
            }
        });
    }

    ngOnInit() {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

        if (!(this.isCompact() || this.isSlim() || this.isHorizontal()) && this.item.routerLink) {
            this.updateActiveStateFromRoute();
        }
    }

    ngOnChanges() {
        // The menu model is rebuilt whenever the selected client/app/customer
        // changes, which rewrites the routerLinks — re-match against the URL.
        this.updateRouteActive();
    }

    ngAfterViewChecked() {
        if (this.root && this.active && this.isDesktop && (this.isHorizontal() || this.isSlim() || this.isCompact())) {
            this.calculatePosition(this.submenu?.nativeElement, this.submenu?.nativeElement.parentElement);
        }
    }

    updateActiveStateFromRoute() {
        if (this.item && this.item.routerLink) {
            let activeRoute = this.router.isActive(this.item.routerLink[0], {
                paths: 'exact',
                queryParams: 'ignored',
                matrixParams: 'ignored',
                fragment: 'ignored'
            });
            if (activeRoute) {
                this.layoutService.onMenuStateChange({
                    key: this.key,
                    routeEvent: true
                });
            }
        }
    }

    /** Highlight this item when its own link, or any descendant's link, matches the URL. */
    updateRouteActive() {
        this.routeActive = this.isItemRouteActive(this.item);
    }

    private isItemRouteActive(item: any): boolean {
        if (!this.isVisibleItem(item)) return false;
        if (this.isLinkActive(item.routerLink) || this.matchesActiveRoutes(item)) return true;
        return (item.items || []).some((child: any) => this.isItemRouteActive(child));
    }

    /* Escape hatch for items whose routerLink cannot describe every page they
       own — File List, for instance, points at /home for a Cozentus user but
       the grid itself lives under /home/extraction/idp-doc-grid/:cid. The item
       names the URL segments that belong to it instead. */
    private matchesActiveRoutes(item: any): boolean {
        const patterns: string[] = item.activeRoutes || [];
        if (!patterns.length) return false;

        const segments = this.stripUrl(this.router.url).split('/').filter(Boolean);
        return patterns.some((pattern) => segments.includes(pattern));
    }

    private isLinkActive(routerLink: any): boolean {
        if (!routerLink) return false;

        const commands = Array.isArray(routerLink) ? routerLink : [routerLink];
        let linkPath: string;
        try {
            linkPath = this.stripUrl(this.router.serializeUrl(this.router.createUrlTree(commands)));
        } catch {
            return false;
        }

        const currentPath = this.stripUrl(this.router.url);
        if (currentPath === linkPath) return true;

        // Drill-down pages (e.g. a code list opened from Code List Management)
        // keep their menu entry lit. Guarded to multi-segment links so a link
        // like `/home` does not claim every page beneath it.
        const isDeepLink = linkPath.split('/').filter(Boolean).length > 1;
        return isDeepLink && currentPath.startsWith(linkPath + '/');
    }

    private stripUrl(url: string): string {
        return url.split('?')[0].split('#')[0];
    }

    onSubmenuAnimated(event: AnimationEvent) {
        if (event.toState === 'visible' && this.isDesktop && (this.isHorizontal() || this.isSlim() || this.isCompact())) {
            const el = <HTMLUListElement>event.element;
            const elParent = <HTMLUListElement>el.parentElement;
            this.calculatePosition(el, elParent);
        }
    }

    calculatePosition(overlay: HTMLElement, target: HTMLElement) {
        if (overlay) {
            const { left, top } = target.getBoundingClientRect();
            const [vWidth, vHeight] = [window.innerWidth, window.innerHeight];
            const [oWidth, oHeight] = [overlay.offsetWidth, overlay.offsetHeight];
            const scrollbarWidth = DomHandler.calculateScrollbarWidth();
            // reset
            overlay.style.top = '';
            overlay.style.left = '';

            if (this.layoutService.isHorizontal()) {
                const width = left + oWidth + scrollbarWidth;
                overlay.style.left = vWidth < width ? `${left - (width - vWidth)}px` : `${left}px`;
            } else if (this.layoutService.isSlim() || this.layoutService.isCompact()) {
                const height = top + oHeight;
                overlay.style.top = vHeight < height ? `${top - (height - vHeight)}px` : `${top}px`;
            }
        }
    }

    itemClick(event: Event) {
        // avoid processing disabled items
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // direct href navigation items (non-router, no children): let browser handle reliably
        if (this.item.url && !this.item.routerLink && !this.item.items) {
            event.stopPropagation();
            event.preventDefault();
            const target = this.item.target || '_self';
            window.open(this.item.url, target);
            if (target !== '_self') {
                // if opening in new tab/window, we can safely reset menu state asynchronously
                setTimeout(() => {
                    if (this.layoutService.isMobile() || this.isSlim() || this.isHorizontal() || this.isCompact()) {
                        this.layoutService.reset();
                        this.layoutService.layoutState.update((val) => ({ ...val, menuHoverActive: false }));
                    }
                }, 0);
            }
            return;
        }

        // navigate with hover
        if ((this.root && this.isSlim()) || this.isHorizontal() || this.isCompact()) {
            this.layoutService.layoutState.update((val) => ({
                ...val,
                menuHoverActive: !val.menuHoverActive
            }));
        }

        // execute command
        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        // toggle active state
        if (this.item.items) {
            this.active = !this.active;

            if (this.root && this.active && (this.isSlim() || this.isHorizontal() || this.isCompact())) {
                this.layoutService.onOverlaySubmenuOpen();
            }
        } else {
            if (this.layoutService.isMobile()) {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    staticMenuMobileActive: false
                }));
            }

            if (this.isSlim() || this.isHorizontal() || this.isCompact()) {
                this.layoutService.reset();
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    menuHoverActive: false
                }));
            }
        }

        this.layoutService.onMenuStateChange({ key: this.key });
    }

    onMouseEnter() {
        // activate item on hover
        if (!this.layoutService.isDesktop() || !this.root) return;

        // Clear any pending close timeouts from other menu items
        this.layoutService.clearHoverTimeouts();

        this.isHoveringMenuItem = true;

        // In compact (vertical collapsed) mode, always open on hover
        if (this.isCompact()) {
            this.active = true;
            this.layoutService.onMenuStateChange({ key: this.key });
            return;
        }

        // In slim/horizontal modes, respect the hover activation toggle
        if (this.isSlim() || this.isHorizontal()) {
            if (this.layoutService.layoutState().menuHoverActive) {
                this.active = true;
                this.layoutService.onMenuStateChange({ key: this.key });
            }
        }
    }

    onMouseLeave() {
        // Handle mouse leave with delay to allow moving to submenu
        if (!this.layoutService.isDesktop() || !this.root) return;

        this.isHoveringMenuItem = false;
        this.scheduleSubmenuClose();
    }

    onSubmenuMouseEnter() {
        this.isHoveringSubmenu = true;
        this.clearHoverTimeout();
    }

    onSubmenuMouseLeave() {
        this.isHoveringSubmenu = false;
        this.scheduleSubmenuClose();
    }

    private clearHoverTimeout() {
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
        }
    }

    private scheduleSubmenuClose() {
        this.clearHoverTimeout();
        // Only close if mouse has left both menu item and submenu
        if (!this.isHoveringMenuItem && !this.isHoveringSubmenu) {
            this.hoverTimeout = setTimeout(() => {
                if (this.isCompact() || this.isSlim() || this.isHorizontal()) {
                    this.active = false;
                    // Don't call onMenuStateChange here as it causes flickering
                    // when moving between menu items. Let the menu system handle it.
                }
            }, 300); // 300ms delay to allow mouse movement
        }
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }

        if (this.clearHoverTimeoutSubscription) {
            this.clearHoverTimeoutSubscription.unsubscribe();
        }

        if (this.routerEventsSubscription) {
            this.routerEventsSubscription.unsubscribe();
        }

        // Clear any pending hover timeout
        this.clearHoverTimeout();
    }

    shouldShowTooltip(): boolean {
        if (!this.root) return false;
        // Don't show tooltip for items with submenu in compact mode
        if (this.isCompact() && this.item.items) return false;
        // Disable in horizontal mode; enable for compact and slim where labels are truncated
        return this.isCompact() || this.isSlim();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        // Only apply click-outside logic in compact mode for root items with active submenus
        if (!this.root || !this.active || !this.item.items) return;
        
        // Only apply in compact mode
        if (!this.layoutService.isCompact()) {
            return;
        }

        // Check if click is outside this menu item and its submenu
        const clickedElement = event.target as HTMLElement;
        const hostElement = this.el.nativeElement;
        
        if (hostElement && !hostElement.contains(clickedElement)) {
            // Close this submenu
            this.active = false;
            this.layoutService.onMenuStateChange({ key: '' });
        }
    }
}
